import { useEffect } from 'react';
import { isWeb } from '@helpers';

export interface UseKeyboardShortcutsParams {
  isEditingText: boolean;
  hasSelection: boolean;
  onDeleteSelected: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onEscape: () => void;
}

/**
 * Web-only: Cmd/Ctrl+Z, +Shift+Z, Delete/Backspace (when a selection exists), Escape.
 * No-op on native (keyboard events route through TextInput there).
 */
export const useKeyboardShortcuts = ({
  isEditingText,
  hasSelection,
  onDeleteSelected,
  onUndo,
  onRedo,
  onEscape,
}: UseKeyboardShortcutsParams) => {
  useEffect(() => {
    if (!isWeb) return undefined;

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

      if ((e.key === 'Delete' || e.key === 'Backspace') && hasSelection && !isEditingText) {
        e.preventDefault();
        onDeleteSelected();
        return;
      }
      if (e.key === 'Escape') {
        onEscape();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) onRedo();
        else onUndo();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isEditingText, hasSelection, onDeleteSelected, onUndo, onRedo, onEscape]);
};
